import { useEffect, useState } from 'react'
import { Check, Copy, ExternalLink, Eye, EyeOff, Lock, MessagesSquare } from 'lucide-react'
import { api } from '@/services/api'

interface TeamLink {
  id: number
  equipe: string
  url: string
  password: string | null
}

interface ClientWorkshop {
  workshop_id: number
  date: string
  team_links: TeamLink[]
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

function TeamLinkRow({ link }: { link: TeamLink }) {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)

  const copyPassword = async () => {
    if (!link.password) return
    try {
      await navigator.clipboard.writeText(link.password)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      console.error('Copie du mot de passe impossible:', error)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
      <span className="min-w-[140px] flex-1 text-sm font-medium text-brand-navy">{link.equipe}</span>
      {link.password && (
        <div className="flex items-center gap-1 rounded-[10px] border border-gray-200 bg-brand-canvas py-1 pl-2.5 pr-1">
          <Lock className="h-3.5 w-3.5 text-gray-500" aria-hidden />
          <span className={`min-w-[96px] font-mono text-[13px] text-brand-navy ${shown ? '' : 'tracking-[2px]'}`}>
            {shown ? link.password : '••••••••'}
          </span>
          <button
            type="button"
            onClick={() => setShown((value) => !value)}
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-brand-navy"
            aria-label={shown ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          >
            {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={copyPassword}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-white hover:text-brand-navy"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-[#16A34A]" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copié' : 'Copier'}
          </button>
        </div>
      )}
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[10px] bg-brand px-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-dark"
      >
        Ouvrir l'atelier
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  )
}

/**
 * Accès aux ateliers de préparation (workshop.qolaig.app) du client, une ligne par équipe.
 * N'affiche rien si aucun atelier n'est rendu visible par un admin.
 * `previewClientId` : aperçu « Vue client » d'un admin.
 */
export function ClientWorkshopAccessCard({ previewClientId }: { previewClientId?: string }) {
  const [workshops, setWorkshops] = useState<ClientWorkshop[]>([])

  useEffect(() => {
    let cancelled = false
    const params = previewClientId ? { client_id: previewClientId } : undefined
    api
      .get<{ data: ClientWorkshop[]; count: number }>('/workshops/client-links', params)
      .then((response) => {
        if (!cancelled) setWorkshops(response.data?.data || [])
      })
      .catch((error) => {
        console.error('Erreur lors de la récupération des accès atelier:', error)
        if (!cancelled) setWorkshops([])
      })
    return () => {
      cancelled = true
    }
  }, [previewClientId])

  if (workshops.length === 0) return null
  const showDates = workshops.length > 1

  return (
    <section
      aria-labelledby="atelier-preparation-title"
      className="mb-5 flex flex-wrap gap-4 rounded-2xl border border-gray-200 bg-white p-5"
    >
      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand-light">
        <MessagesSquare className="h-[22px] w-[22px] text-brand" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="atelier-preparation-title" className="text-[15px] font-semibold text-brand-navy">
            Atelier de préparation
          </h2>
          <span className="rounded-full bg-[#E7F6EE] px-2 py-0.5 text-[11px] font-medium text-[#0B6B3A]">Disponible</span>
        </div>
        <p className="mt-0.5 text-[13px] leading-relaxed text-gray-600">
          Les tâches répétitives documentées par vos équipes avec notre assistant IA.
        </p>
        <div className="mt-2 divide-y divide-gray-100">
          {workshops.map((workshop) => (
            <div key={workshop.workshop_id}>
              {showDates && (
                <p className="pt-3 text-xs font-medium text-gray-500">Atelier du {formatDate(workshop.date)}</p>
              )}
              <div className="divide-y divide-gray-100">
                {workshop.team_links.map((link) => (
                  <TeamLinkRow key={link.id} link={link} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
